import { Eye, Lock, Plus, ShieldOff, ShieldPlus } from 'lucide-react';
import { ROLE_LABELS, expandPermissions } from '@supernova/shared';
import { PageHeader } from '@/components/shared/page-header';
import { EmptyState } from '@/components/shared/empty-state';
import { useTenantRoles } from '@/hooks/use-tenant-roles';
import { BUILT_IN, BUILT_IN_SUMMARY, useUserAccess } from './user-access';

const ROLES_HASH = '#/users/roles';

const th = 'px-4 py-2.5 text-left text-xs font-medium text-muted-foreground';
const td = 'px-4 py-3 text-sm align-middle';
const smallButton =
  'inline-flex h-8 items-center rounded-lg border border-border bg-white px-3 text-xs font-medium text-foreground hover:bg-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring';

function Card({ title, description, children }: { title: string; description: string; children: React.ReactNode }) {
  return (
    <section className="relative overflow-hidden rounded-xl border border-border bg-white">
      <div className="border-b border-border px-5 py-4">
        <h2 className="text-base font-semibold">{title}</h2>
        <p className="mt-0.5 text-sm text-muted-foreground">{description}</p>
      </div>
      {children}
    </section>
  );
}

/** Every role the business can give: its own custom roles, then the fixed built-in ones. */
export function RolesPage() {
  const { canManageRoles } = useUserAccess();
  const { data, isLoading } = useTenantRoles({ enabled: canManageRoles });
  const roles = data?.data ?? [];

  const header = (
    <>
      <nav aria-label="Breadcrumb" className="text-xs text-muted-foreground">
        <a href="#/users" className="hover:text-foreground hover:underline">
          User Management
        </a>
        <span aria-hidden="true"> / </span>
        <span className="text-foreground">Roles and Permissions</span>
      </nav>
      <PageHeader
        title="Roles and Permissions"
        description="Choose what each role can see and do. Each permission matches a screen or button in the sidebar."
        backTo="#/users"
        backLabel="Back to User Management"
      >
        {canManageRoles && (
          <>
            <a
              href={`${ROLES_HASH}/new?from=view-only`}
              className="inline-flex items-center gap-2 whitespace-nowrap rounded-lg border border-border bg-white px-4 py-2 text-sm font-medium hover:bg-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            >
              <Eye className="h-4 w-4" aria-hidden="true" />
              New view-only role
            </a>
            <a
              href={`${ROLES_HASH}/new`}
              className="inline-flex items-center gap-2 whitespace-nowrap rounded-lg bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
            >
              <Plus className="h-4 w-4" aria-hidden="true" />
              New Role
            </a>
          </>
        )}
      </PageHeader>
    </>
  );

  if (!canManageRoles) {
    return (
      <div className="space-y-4">
        {header}
        <div className="rounded-xl border border-border bg-white">
          <EmptyState icon={ShieldOff} title="You can't manage roles" description="Only owners and admins can." />
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-5">
      {header}

      <Card title="Custom roles" description="Roles your business made. You can edit or delete them.">
        {isLoading ? (
          <div className="flex h-32 items-center justify-center text-sm text-muted-foreground">Loading...</div>
        ) : roles.length === 0 ? (
          <EmptyState
            icon={ShieldPlus}
            title="No custom roles yet"
            description="Make one when a built-in role gives too much or too little."
            action={
              <a href={`${ROLES_HASH}/new`} className={smallButton}>
                New Role
              </a>
            }
          />
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full min-w-[40rem] border-collapse">
              <thead className="bg-gray-50">
                <tr className="border-b border-border">
                  <th className={th}>Role</th>
                  <th className={th}>Treated as on the register</th>
                  <th className={th}>Permissions</th>
                  <th className={th}>Users</th>
                  <th className={th}>
                    <span className="block text-right">Actions</span>
                  </th>
                </tr>
              </thead>
              <tbody>
                {roles.map((role) => (
                  <tr key={role.id} className="border-b border-border/60 last:border-0 hover:bg-gray-50">
                    <td className={`${td} font-semibold`}>{role.name}</td>
                    <td className={td}>
                      <span className="inline-flex h-6 items-center rounded-md border border-border px-2 text-xs font-medium">
                        {ROLE_LABELS[role.baseRole] ?? role.baseRole}
                      </span>
                    </td>
                    <td className={td}>{expandPermissions(role.permissions).length}</td>
                    <td className={`${td} text-muted-foreground`}>
                      {role.userCount === 0
                        ? 'No users'
                        : `${role.userCount} ${role.userCount === 1 ? 'user' : 'users'}`}
                    </td>
                    <td className={`${td} whitespace-nowrap text-right`}>
                      <div className="flex justify-end gap-2">
                        <a href={`${ROLES_HASH}/${role.id}`} className={smallButton} aria-label={`Edit ${role.name}`}>
                          Edit
                        </a>
                        <a
                          href={`${ROLES_HASH}/new?copy=custom:${role.id}`}
                          className={smallButton}
                          aria-label={`Duplicate ${role.name}`}
                        >
                          Duplicate
                        </a>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      <Card title="Built-in roles" description="The same for every business. Duplicate one to start a custom role.">
        <div className="overflow-x-auto">
          <table className="w-full min-w-[36rem] border-collapse">
            <thead className="bg-gray-50">
              <tr className="border-b border-border">
                <th className={th}>Role</th>
                <th className={th}>What it covers</th>
                <th className={th}>
                  <span className="block text-right">Actions</span>
                </th>
              </tr>
            </thead>
            <tbody>
              {BUILT_IN.map((role) => (
                <tr key={role} className="border-b border-border/60 last:border-0 hover:bg-gray-50">
                  <td className={td}>
                    <span className="flex items-center gap-2 font-semibold">
                      <Lock className="h-3.5 w-3.5 text-muted-foreground" aria-label="Built-in, can't be changed" />
                      {ROLE_LABELS[role]}
                    </span>
                  </td>
                  <td className={`${td} text-muted-foreground`}>{BUILT_IN_SUMMARY[role]}</td>
                  <td className={`${td} whitespace-nowrap text-right`}>
                    <div className="flex justify-end gap-2">
                      <a
                        href={`${ROLES_HASH}/${role}`}
                        className={smallButton}
                        aria-label={`View ${ROLE_LABELS[role]}`}
                      >
                        View
                      </a>
                      <a
                        href={`${ROLES_HASH}/new?copy=${role}`}
                        className={smallButton}
                        aria-label={`Duplicate ${ROLE_LABELS[role]}`}
                      >
                        Duplicate
                      </a>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>
    </div>
  );
}
